import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService, courseTreeApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';

// Toast notification component
const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
      type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'error' ? (
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          )}
          <span className="text-sm font-medium">{message}</span>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface Tag {
  tagId: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  term: string;
  name: string;
  description: string | null;
  createdAt: string;
}

interface QuizGenerationRequest {
  title: string;
  description: string;
  createdByTeacherId: string;
  totalQuestions: number;
  timeLimit: number;
  generationMethod: 'Random';
  targetUnitId: number;
  selectionCriteria: {
    requiredTagIds: string[];
  };
}

interface Course {
  id: string;
  teacherId: string;
  teacherName: string;
  subjectId: string;
  subjectName: string;
  term: string;
  courseName: string;
  description: string;
  active: boolean;
  price: number;
  grade: string;
  imagePath: string | null;
  isOpenToAll: boolean;
  createdDate: string;
  modificationDate: string;
  units: Unit[];
}

interface Unit {
  id: number;
  title: string;
  lessons: Lesson[];
  isVisible: boolean;
}

interface Lesson {
  id: number;
  title: string;
  type: 'video' | 'quiz';
  isVisible: boolean;
  order?: number;
}

interface ImportCourseSummary {
  id: string;
  name: string;
}

const RandomQuiz = () => {
  const { courseId, unitId } = useParams<{ courseId: string; unitId?: string }>();
  const navigate = useNavigate();
  const { user, isTeacher, isModerator } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [courseDetails, setCourseDetails] = useState<Course | null>(null);
  const [generating, setGenerating] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');
  const [timeLimit, setTimeLimit] = useState(60);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [importCourses, setImportCourses] = useState<ImportCourseSummary[]>([]);
  const [importCoursesLoading, setImportCoursesLoading] = useState(false);
  const [selectedImportCourseId, setSelectedImportCourseId] = useState<string>('');
  const [importUnits, setImportUnits] = useState<Unit[]>([]);
  const [selectedImportUnitId, setSelectedImportUnitId] = useState<number | null>(null);
  const [importQuizzes, setImportQuizzes] = useState<Lesson[]>([]);
  const [selectedImportQuizId, setSelectedImportQuizId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);

  const fetchData = useCallback(async () => {
    console.log('Fetching data for courseId:', courseId);
    setLoading(true);
    setError(null);

    try {
      // First fetch course details
      const courseResponse = await authFetch(apiUrl(`Course/${courseId}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const courseData = await courseResponse.json();
      console.log('Course API response:', courseData);

      if (!courseData.success) {
        throw new Error(courseData.message || 'Failed to fetch course details');
      }

      setCourseDetails(courseData.data);

      // Set units from course data
      if (courseData.data.units) {
        setUnits(courseData.data.units.map((unit: any) => ({
          id: unit.id,
          title: unit.unitName || unit.title,
          lessons: unit.lessons || [],
          isVisible: unit.isVisible !== false
        })));
      }

      // Set selectedUnitId from URL parameter if provided
      if (unitId) {
        setSelectedUnitId(Number(unitId));
      }

      // Check if user can access this course
      const canAccess = isModerator() ||
        (isTeacher() && user && user.id === courseData.data.teacherId);

      if (!canAccess) {
        throw new Error('ليس لديك صلاحية للوصول إلى هذا المحتوى');
      }

      // Then fetch tags using course details
      const tagsResponse = await authFetch(
        apiUrl(`Tag?TeacherId=${courseData.data.teacherId}&SubjectId=${courseData.data.subjectId}&Term=${courseData.data.term}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const tagsData = await tagsResponse.json();
      console.log('Tags API response:', tagsData);

      if (!tagsData.success) {
        throw new Error(tagsData.message || 'Failed to fetch tags');
      }

      setTags(tagsData.data.tags);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [courseId, unitId, user, isModerator, isTeacher]);

  // Fetch course details and tags
  useEffect(() => {
    if (courseId) {
      fetchData();
    }
  }, [courseId, fetchData]);

  // Fetch courses for import (for moderators: all, for teachers: their courses – handled by backend)
  useEffect(() => {
    const fetchImportCourses = async () => {
      setImportCoursesLoading(true);
      try {
        const response = await authFetch(
          apiUrl('Course/all?pageNumber=1&pageSize=100'),
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authService.getToken()}`
            }
          }
        );

        const data = await response.json();

        if (data.success && data.data && Array.isArray(data.data.data)) {
          const courseData = data.data;
          const formatted: ImportCourseSummary[] = courseData.data.map((course: any) => ({
            id: course.id,
            name: course.courseName
          }));
          setImportCourses(formatted);
        }
      } catch (err) {
        console.error('Error fetching import courses:', err);
      } finally {
        setImportCoursesLoading(false);
      }
    };

    fetchImportCourses();
  }, []);

  const fetchImportUnitsForCourse = async (courseIdForImport: string) => {
    setImportUnits([]);
    setSelectedImportUnitId(null);
    setImportQuizzes([]);
    setSelectedImportQuizId(null);

    if (!courseIdForImport) return;

    try {
      const data = await courseTreeApi.getCourseTree(courseIdForImport);
      if (data.success && data.data && Array.isArray(data.data.units)) {
        const mappedUnits: Unit[] = data.data.units.map((unit: any) => ({
          id: unit.id,
          title: unit.unitName || unit.titel || unit.title,
          lessons: (unit.lessons || []).map((lesson: any) => ({
            id: lesson.id,
            title: lesson.lessonName || lesson.titel || lesson.title,
            type:
              (lesson.type || '').toLowerCase() === 'video'
                ? 'video'
                : 'quiz',
            isVisible: lesson.active,
            order: lesson.order
          })),
          isVisible: unit.active !== false
        }));
        setImportUnits(mappedUnits);
      }
    } catch (err) {
      console.error('Error fetching units for import course:', err);
    }
  };

  // Update quizzes when import unit changes
  useEffect(() => {
    if (!selectedImportUnitId) {
      setImportQuizzes([]);
      setSelectedImportQuizId(null);
      return;
    }

    const unit = importUnits.find(u => u.id === selectedImportUnitId);
    if (unit && unit.lessons) {
      setImportQuizzes(unit.lessons.filter(lesson => lesson.type === 'quiz'));
    } else {
      setImportQuizzes([]);
    }
    setSelectedImportQuizId(null);
  }, [selectedImportUnitId, importUnits]);


  const handleTagToggle = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const handleGenerateQuiz = async () => {
    if (!courseDetails || selectedTags.length === 0) return;
    if (!selectedUnitId) {
      showToast('يجب تحديد الوحدة قبل إنشاء الامتحان', 'error');
      return;
    }
    if (!quizTitle.trim()) {
      showToast('يجب إدخال عنوان الامتحان', 'error');
      return;
    }

    setGenerating(true);
    setError(null); // Clear any previous errors
    
    try {
      const requestBody: QuizGenerationRequest = {
        title: quizTitle,
        description: quizDescription || "امتحان تم إنشاؤه تلقائياً من بنك الأسئلة",
        createdByTeacherId: courseDetails.teacherId,
        totalQuestions: totalQuestions,
        timeLimit: timeLimit,
        generationMethod: "Random",
        targetUnitId: selectedUnitId, // Use selected unit ID
        selectionCriteria: {
          requiredTagIds: selectedTags
        }
      };

      const response = await authFetch(apiUrl('QuizGeneration/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        showToast('تم إنشاء الامتحان بنجاح', 'success');
        // Navigate back to course detail page after a short delay
        setTimeout(() => {
          navigate(`/courses/${courseId}`);
        }, 1500);
      } else {
        // Show API error message as toast without clearing form data
        showToast(data.message || 'فشل في إنشاء الامتحان', 'error');
      }
    } catch (err) {
      console.error('Error generating quiz:', err);
      showToast('حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleImportQuiz = async () => {
    if (!courseDetails || !selectedUnitId) {
      showToast('يجب تحديد الوحدة المستهدفة قبل استيراد الاختبار', 'error');
      return;
    }

    if (!selectedImportCourseId || !selectedImportUnitId || !selectedImportQuizId) {
      showToast('يرجى اختيار الدورة والوحدة والاختبار المراد استيراده', 'error');
      return;
    }

    if (!quizTitle.trim()) {
      showToast('يجب إدخال عنوان للاختبار الجديد', 'error');
      return;
    }

    setImporting(true);

    try {
      const lessonResponse = await authFetch(apiUrl(`Lesson/${selectedImportQuizId}`), {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (!lessonResponse.ok) {
        throw new Error(`HTTP error! status: ${lessonResponse.status}`);
      }

      const responseText = await lessonResponse.text();
      let lessonResult: any;
      try {
        lessonResult = JSON.parse(responseText);
      } catch (e) {
        console.error('JSON parse error while importing quiz lesson:', e);
        throw new Error('استجابة غير صالحة من الخادم عند جلب بيانات الاختبار');
      }

      if (!lessonResult.success || !lessonResult.data) {
        throw new Error(lessonResult.message || 'فشل في جلب بيانات الاختبار المحدد');
      }

      const sourceLesson = lessonResult.data;

      const targetUnit = units.find(u => u.id === selectedUnitId);
      const lastOrder =
        targetUnit && targetUnit.lessons && targetUnit.lessons.length > 0
          ? Math.max(...targetUnit.lessons.map(l => l.order || 0))
          : 0;

      const newLessonName =
        quizTitle.trim() ||
        sourceLesson.lessonName ||
        sourceLesson.titel ||
        'اختبار منسوخ';

      // API requires exactly 4 answers per multiple choice question – normalize before POST
      const rawQuestions = Array.isArray(sourceLesson.questions) ? sourceLesson.questions : [];
      const normalizeAnswers = (answers: { text: string; isCorrect: boolean }[], _type: string) => {
        const list = (answers || []).slice(0, 4);
        while (list.length < 4) {
          list.push({ text: '', isCorrect: false });
        }
        return list;
      };
      const mapImportedQuestionType = (q: any, answers: any[]): 'MultipleChoice' | 'TrueFalse' => {
        const raw = q?.type ?? q?.questionType ?? '';
        const normalized = raw.toString().toLowerCase().replace(/[_\s-]/g, '');
        if (normalized.includes('truefalse') || normalized === 'tf' || normalized === '1') {
          return 'TrueFalse';
        }
        if (normalized.includes('multiple') || normalized.includes('choice')) {
          return 'MultipleChoice';
        }
        const texts = answers
          .map((a: any) => (a?.text ?? '').toString().trim().toLowerCase())
          .filter(Boolean);
        if (texts.length === 2) {
          const set = new Set(texts);
          if (
            (set.has('true') && set.has('false')) ||
            (set.has('صح') && set.has('خطأ')) ||
            (set.has('صواب') && set.has('خطأ'))
          ) {
            return 'TrueFalse';
          }
        }
        return 'MultipleChoice';
      };
      const questionsPayload = rawQuestions.map((q: any) => {
        const answers = Array.isArray(q.answers) ? q.answers : [];
        const type = mapImportedQuestionType(q, answers);
        const usableAnswers =
          type === 'TrueFalse'
            ? answers.filter((a: any) => (a?.text ?? '').toString().trim() !== '').slice(0, 2)
            : answers;
        return {
          text: q.text,
          type,
          answers: type === 'MultipleChoice' ? normalizeAnswers(usableAnswers, type) : usableAnswers
        };
      });

      const addResponse = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: newLessonName,
          titel: newLessonName,
          unitId: selectedUnitId,
          active: true,
          order: lastOrder + 1,
          type: 'quiz',
          questions: questionsPayload
        })
      });

      const addData = await addResponse.json();

      if (addData.success) {
        showToast('تم استيراد الاختبار وإنشاؤه بنجاح', 'success');
        setTimeout(() => {
          navigate(`/courses/${courseId}`);
        }, 1500);
      } else {
        showToast(addData.message || 'فشل في استيراد الاختبار', 'error');
      }
    } catch (err) {
      console.error('Error importing quiz:', err);
      showToast(
        err instanceof Error
          ? err.message
          : 'حدث خطأ أثناء استيراد الاختبار. يرجى المحاولة مرة أخرى',
        'error'
      );
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="text-red-600 mb-4">
            <svg className="w-6 h-6 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
          <button
            onClick={() => navigate(`/courses/${courseId}`)}
            className="text-blue-600 hover:text-blue-800"
          >
            العودة إلى صفحة الدورة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">إنشاء امتحان من بنك الأسئلة</h1>
              {courseDetails && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    الدورة: <span className="font-medium">{courseDetails.courseName}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    المعلم: <span className="font-medium">{courseDetails.teacherName}</span>
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(`/courses/${courseId}`)}
              className="text-gray-600 hover:text-gray-800"
            >
              العودة إلى صفحة الدورة
            </button>
          </div>

          {/* Unit Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اختر الوحدة:
            </label>
            <select
              value={selectedUnitId || ''}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedUnitId(Number(e.target.value) || null)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">اختر الوحدة</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>
                  {unit.title}
                </option>
              ))}
            </select>
          </div>

          {/* Import existing quiz from another lesson */}
          <div className="mb-8 border border-blue-100 rounded-lg p-4 bg-blue-50/40">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              استيراد اختبار من درس آخر (اختياري)
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              يمكنك اختيار دورة ووحدة واختبار موجود لنسخ أسئلته وإنشاء اختبار جديد في الوحدة المحددة أعلاه.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اختر الدورة:
                </label>
                <select
                  value={selectedImportCourseId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value;
                    setSelectedImportCourseId(value);
                    fetchImportUnitsForCourse(value);
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={importCoursesLoading}
                >
                  <option value="">
                    {importCoursesLoading ? 'جاري تحميل الدورات...' : 'اختر الدورة'}
                  </option>
                  {importCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اختر الوحدة (من الدورة المحددة):
                </label>
                <select
                  value={selectedImportUnitId || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSelectedImportUnitId(Number(e.target.value) || null)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!selectedImportCourseId || importUnits.length === 0}
                >
                  <option value="">اختر الوحدة</option>
                  {importUnits.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اختر الاختبار (الدرس):
                </label>
                <select
                  value={selectedImportQuizId || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setSelectedImportQuizId(Number(e.target.value) || null)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!selectedImportUnitId || importQuizzes.length === 0}
                >
                  <option value="">اختر الاختبار</option>
                  {importQuizzes.map(lesson => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Teacher Name Display */}
          {courseDetails && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">
                المعلم:
              </label>
              <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
                {courseDetails.teacherName}
              </div>
            </div>
          )}

          {/* Quiz Details Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                عنوان الامتحان:
              </label>
              <input
                type="text"
                value={quizTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuizTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="أدخل عنوان الامتحان"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                عدد الأسئلة:
              </label>
              <input
                type="number"
                value={totalQuestions}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalQuestions(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
                max="100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                مدة الامتحان (بالدقائق):
              </label>
              <input
                type="number"
                value={timeLimit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTimeLimit(Number(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
                max="300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                وصف الامتحان:
              </label>
              <textarea
                value={quizDescription}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuizDescription(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="أدخل وصف الامتحان (اختياري)"
                rows={3}
              />
            </div>
          </div>

          {/* Tags Checkbox List */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              اختر الدروس المطلوبة للامتحان:
            </label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
              {tags.length === 0 ? (
                <p className="text-gray-500 text-center py-4">لا توجد دروس متاحة</p>
              ) : (
                <div className="space-y-3">
                  {tags.map(tag => (
                    <label key={tag.tagId} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag.tagId)}
                        onChange={() => handleTagToggle(tag.tagId)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{tag.name}</span>
                        {tag.description && (
                          <p className="text-xs text-gray-500 mt-1">{tag.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-3 text-sm text-gray-600">
                تم اختيار {selectedTags.length} درس
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row justify-end gap-4">
            <button
              onClick={handleImportQuiz}
              disabled={
                !selectedUnitId ||
                importing ||
                !quizTitle.trim() ||
                !selectedImportCourseId ||
                !selectedImportUnitId ||
                !selectedImportQuizId
              }
              className={`px-6 py-2 rounded-lg text-white font-medium transition-all ${
                !selectedUnitId ||
                importing ||
                !quizTitle.trim() ||
                !selectedImportCourseId ||
                !selectedImportUnitId ||
                !selectedImportQuizId
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {importing ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  جاري استيراد الاختبار...
                </span>
              ) : (
                'استيراد اختبار من درس'
              )}
            </button>

            <button
              onClick={handleGenerateQuiz}
              disabled={selectedTags.length === 0 || !selectedUnitId || generating || !quizTitle.trim()}
              className={`px-6 py-2 rounded-lg text-white font-medium transition-all ${
                selectedTags.length === 0 || !selectedUnitId || generating || !quizTitle.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {generating ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  جاري إنشاء الامتحان...
                </span>
              ) : (
                'إنشاء امتحان عشوائي'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RandomQuiz;
